import { useState, useEffect, useRef, useCallback } from 'react'
import { api } from '../services/api'
import { localCrisisCheck } from '../services/crisisCheck'
import { resolveRegion, getCrisisResources } from '../services/crisisResources'
import { saveOpenSession, loadOpenSession, clearOpenSession, reconcile } from '../services/sessionStore'
import { getGuidanceVoiceId } from '../services/voices'

export function useSession() {
  const [sessionId, setSessionId] = useState(null)
  const [sessionType, setSessionType] = useState(null)   // tracks which healing session type is active
  const [mode, setMode] = useState('individual')         // 'individual' | 'couples' (gated by FEATURE_SESSION_MODES)
  const [messages, setMessages] = useState([])
  const [loading, setLoading] = useState(false)
  const [sessionLoading, setSessionLoading] = useState(false)
  const [crisis, setCrisis] = useState(false)
  const [careTier, setCareTier] = useState('green')
  const [sessionError, setSessionError] = useState(null)

  // ── Recovery ──────────────────────────────────────────────────────────────
  // An unfinished conversation found either on this device or on the server.
  // We do NOT auto-restore it: being dropped straight back into something heavy
  // without warning is its own harm. The view offers it and the person chooses.
  const [recoverable, setRecoverable] = useState(null)
  const restoredRef = useRef(false)

  useEffect(() => {
    let alive = true
    const local = loadOpenSession()
    // Show the local copy immediately (works offline), then let the server
    // confirm or better it.
    if (local) setRecoverable(local)
    api.get('/session/open')
      .then(r => {
        if (!alive) return
        const merged = reconcile(local, r?.open || null)
        if (merged?.sessionId) setRecoverable(merged)
        else if (!local) setRecoverable(null)
      })
      .catch(() => { /* offline: the local copy above still stands */ })
    return () => { alive = false }
  }, [])

  const resumeSession = useCallback(() => {
    if (!recoverable) return
    restoredRef.current = true
    setSessionId(recoverable.sessionId)
    setSessionType(recoverable.sessionType || 'REFLECTION')
    if (recoverable.mode) setMode(recoverable.mode)
    setMessages((recoverable.messages || []).map((m, i) => ({
      id: m.id || Date.now() + i, role: m.role, content: m.content, agent: m.agent || null,
    })))
    setRecoverable(null)
  }, [recoverable])

  const discardRecoverable = useCallback(() => {
    clearOpenSession()
    setRecoverable(null)
  }, [])

  // Write the conversation to the device on EVERY change. This is the line that
  // makes switching the screen off safe: whatever happens to the page next, the
  // turns are already on disk.
  useEffect(() => {
    if (!sessionId) return
    saveOpenSession({ sessionId, sessionType, mode, messages })
  }, [sessionId, sessionType, mode, messages])

  // sessionTypeOverride: optional — pass the session type when starting (e.g. 'BREATHWORK', 'SOMATIC')
  const startSession = async (sessionTypeOverride = null) => {
    setSessionLoading(true)
    setSessionError(null)
    if (sessionTypeOverride) setSessionType(sessionTypeOverride)
    try {
      const { sessionId: id } = await api.post('/session/start', {
        sessionType: sessionTypeOverride || sessionType || 'REFLECTION',
      })
      setSessionId(id)
      setMessages([])
      setCrisis(false)
      setCareTier('green')
      return id
    } catch (err) {
      console.error('Session start failed:', err.message)
      setSessionError('Could not start session. Please try again.')
      return null
    } finally {
      setSessionLoading(false)
    }
  }

  const sendMessage = async (text) => {
    if (!sessionId) return null
    setLoading(true)
    const userMsg = { role: 'user', content: text, id: Date.now() }
    setMessages(prev => [...prev, userMsg])

    try {
      // Send the WHOLE session so the companion can summarise/reflect on all of
      // it (the old -10 window made "summarise this session" impossible — the
      // server applies its own token-safe window).
      const history = messages.map(m => ({ role: m.role, content: m.content }))
      const result = await api.post('/session/message', {
        message: text, sessionId, history,
        region: resolveRegion(),                    // region-correct crisis resources server-side
        sessionType: sessionType || 'REFLECTION',   // agent picks the right therapeutic protocol
        mode,                                        // 'individual' | 'couples' (enforced + gated server-side)
        voiceId: getGuidanceVoiceId(),               // ONE companion voice: inline reply audio must
                                                     // match the picker (and /voice/speak chunks)
      })

      // Offline: api.post queued this message for replay — nothing screened it
      // server-side at write time. Run the local crisis net now so an offline
      // disclosure surfaces support immediately (the always-on lifeline is also
      // present). Also avoids rendering a blank assistant bubble for queued sends.
      if (result && result._queued) {
        const offlineCrisis = localCrisisCheck(text)
        if (offlineCrisis) setCrisis(true)
        const res = getCrisisResources(resolveRegion())
        setMessages(prev => [...prev, {
          role: 'assistant',
          id: Date.now() + 1,
          offline: true,
          content: offlineCrisis
            ? `You're offline right now, and your safety matters. Please reach out now — ${res.primary.label} ${res.primary.number}, or ${res.emergency.label} ${res.emergency.number}. Your message is saved, and we'll keep going the moment you're back online.`
            : "You're offline — your message is saved and I'll reply as soon as you're reconnected.",
        }])
        return result
      }

      const assistantMsg = {
        role: 'assistant', content: result.response,
        agent: result.agent, id: Date.now() + 1,
        audio: result.audio || null,
        mimeType: result.mimeType || 'audio/mpeg',
        // Remaining spoken chunks for long replies (couples summaries etc.) —
        // text only; voiced by SessionView as playback reaches them.
        speakRest: Array.isArray(result.speakRest) ? result.speakRest : [],
        suggestions: Array.isArray(result.suggestions) ? result.suggestions : [],
      }
      setMessages(prev => [...prev, assistantMsg])
      if (result.crisis) setCrisis(true)
      // Couples mode is adult-only; if the server blocked it for this account, revert the toggle.
      if (result.modeBlocked === 'minor') setMode('individual')
      setCareTier(result.careTier || 'green')
      return result
    } catch (err) {
      // The server REFUSES a couples turn it cannot run as couples (feature off,
      // account not eligible) rather than running it as a private turn. Say so
      // plainly. The session is NOT switched to Individual: a partner may be in
      // the room, and an individual turn here would carry private memory into
      // a shared conversation. The people end the session instead.
      if (err && (err.message === 'mode_unavailable' || err.message === 'minor_account')) {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: err.message === 'minor_account'
            ? "Couples mode isn't available on this account, so that message wasn't sent. Please end this session."
            : "Couples mode isn't available right now, so that message wasn't sent. Please end this session and try again later.",
          id: Date.now() + 1, error: true,
        }])
        return null
      }
      // Region-correct support line (never another country's number).
      const r = getCrisisResources(resolveRegion())
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `I'm having a moment of difficulty. Please try again — and if you need someone right now, ${r.primary.label} is there: ${r.primary.number}.`,
        id: Date.now() + 1, error: true,
      }])
      return null
    } finally {
      setLoading(false)
    }
  }

  // Determine the most-used agent across all messages
  const dominantAgent = (msgs) => {
    const counts = {}
    msgs.forEach(m => { if (m.agent) counts[m.agent] = (counts[m.agent] || 0) + 1 })
    return Object.keys(counts).sort((a, b) => counts[b] - counts[a])[0] || null
  }

  const endSession = async (finalMessages) => {
    if (!sessionId) return
    const history = (finalMessages || messages)
      .map(m => ({ role: m.role, content: m.content }))
    const agentUsed = dominantAgent(finalMessages || messages)
    try {
      await api.post('/session/end', { sessionId, conversationHistory: history, agentUsed })
    } catch (err) {
      console.error('endSession failed:', err.message)
    }
    // Closed cleanly — there is nothing left to recover, so drop the cache.
    clearOpenSession()
    setSessionId(null)
    setMessages([])
    setCrisis(false)
    setCareTier('green')
  }

  return {
    sessionId, sessionType, setSessionType,
    mode, setMode,
    messages, loading, sessionLoading, sessionError,
    crisis, careTier, startSession, sendMessage, endSession,
    // Recovery of an interrupted conversation.
    recoverable, resumeSession, discardRecoverable,
  }
}
